<script setup lang="ts">
import { onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useCartStore } from '@/stores/cart';

// Cart popover is a *list-only* preview now. Terms, gap checks, and the
// actual order-creation POST all live on /checkout — the cart is just
// "here's what you have, click to review and pay."
const cart = useCartStore();
const router = useRouter();

const totalLabel = computed(() => `${(cart.totalCents / 100).toFixed(2)} €`);

function priceLabel(cents: number) {
  return `${(cents / 100).toFixed(2)} €`;
}

function categoryFor(name: string): string {
  const n = name.toLowerCase();
  if (/knife|karambit|bayonet|huntsman|navaja|stiletto|talon|ursus|paracord|nomad|skeleton|butterfly/.test(n)) return 'Knife';
  if (/glove|wraps/.test(n)) return 'Gloves';
  if (/awp|ssg/.test(n)) return 'Sniper';
  if (/ak-47|m4a4|m4a1|aug|sg|famas|galil/.test(n)) return 'Rifle';
  if (/glock|usp|p2000|p250|five|cz|tec|deagle|desert|r8|berettas/.test(n)) return 'Pistol';
  if (/mp|mac|ump|p90|bizon/.test(n)) return 'SMG';
  return 'Skin';
}

function goToCheckout() {
  cart.close();
  router.push('/checkout');
}

// Esc closes; no backdrop click trap so the rest of the page stays interactive.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && cart.open) cart.close();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <!--
      Right-side panel on lg+ — narrow card pinned to the top-right with no
      backdrop so the rest of the page stays interactive.
      Mobile (< sm) — bottom sheet that fills the viewport width with a
      dim backdrop, since the right-side card would either clip or look
      like it's floating in nowhere on a 375 px screen.
    -->
    <div v-if="cart.open">
      <!-- Mobile backdrop (sm:hidden). Tap to close. -->
      <button
        type="button"
        class="fixed inset-0 z-40 bg-black/60 sm:hidden border-0 cursor-default"
        aria-label="Close cart"
        @click="cart.close()"
      />

      <!-- Panel — full-width sheet on mobile, right-side card on sm+. -->
      <div
        role="dialog"
        aria-label="Cart"
        class="fixed z-50 flex flex-col overflow-hidden shadow-2xl
               inset-x-3 bottom-3 top-auto max-h-[80vh] rounded-[14px]
               sm:inset-auto sm:right-4 sm:bottom-auto sm:top-[120px] sm:w-[360px] sm:max-h-[calc(100vh-150px)] sm:rounded-[12px] lg:right-6"
        style="background: linear-gradient(180deg, #201e2c 0%, #161920 100%); border: 1px solid #2a2f3d;"
      >
      <header class="px-5 pt-5 pb-3 flex items-center justify-between border-b border-[var(--color-border)]">
        <h2 class="font-display text-[22px] text-white leading-none">Cart</h2>
        <button
          class="text-[var(--color-text-dim)] hover:text-white text-[20px] leading-none bg-transparent border-0 cursor-pointer"
          aria-label="Close cart"
          @click="cart.close()"
        >
          ×
        </button>
      </header>

      <!-- Empty state -->
      <div
        v-if="cart.isEmpty"
        class="flex-1 px-5 py-8 flex flex-col items-center justify-center gap-4 text-center"
      >
        <p class="font-body text-[14px] text-[var(--color-text-dim)]">
          Your cart is empty. Browse the marketplace and add a skin to start a checkout.
        </p>
        <button
          class="h-[38px] px-5 rounded-[6px] bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] font-display uppercase text-[13px] text-white transition-colors"
          @click="cart.close(); router.push('/catalog')"
        >
          Browse marketplace
        </button>
      </div>

      <!-- Filled state -->
      <template v-else>
        <ul class="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <li
            v-for="item in cart.items"
            :key="item.listingId"
            class="flex items-center gap-3"
          >
            <!-- Item image -->
            <div class="relative size-[66px] rounded-[6px] overflow-hidden flex-shrink-0 flex items-center justify-center bg-[#0e1117]">
              <span
                aria-hidden="true"
                class="absolute size-[28px] rounded-full"
                style="background: radial-gradient(circle, rgba(73,136,196,0.4) 0%, transparent 70%); top: 50%; left: 50%; transform: translate(-50%, -50%); width: 60px; height: 60px;"
              />
              <img
                :src="item.imageUrl || '/assets/skins/card-placeholder.png'"
                :alt="item.marketHashName"
                class="relative max-w-full max-h-full object-contain"
              />
            </div>

            <div class="flex-1 min-w-0">
              <p class="font-display uppercase text-[8px] text-[var(--color-accent)] leading-none">
                {{ categoryFor(item.marketHashName) }}
              </p>
              <p class="mt-1 font-display text-[12px] text-white leading-tight line-clamp-2">
                {{ item.marketHashName }}
              </p>
              <p class="mt-1 font-display text-[12px] text-[var(--color-accent)]">
                {{ priceLabel(item.ourPriceCents) }}
              </p>
            </div>

            <button
              class="flex-shrink-0 size-[32px] rounded-full bg-transparent border-0 text-[var(--color-error)] hover:text-white cursor-pointer flex items-center justify-center"
              :aria-label="`Remove ${item.marketHashName}`"
              @click="cart.remove(item.listingId)"
            >
              <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                <path d="M2 4H12M5.5 4V2.5C5.5 2.22 5.72 2 6 2H8C8.28 2 8.5 2.22 8.5 2.5V4M3 4V13.5C3 13.78 3.22 14 3.5 14H10.5C10.78 14 11 13.78 11 13.5V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
              </svg>
            </button>
          </li>
        </ul>

        <!-- Footer: total + go-to-checkout -->
        <footer class="border-t border-[var(--color-border)] px-5 py-4 flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <span class="font-display text-[14px] text-white">Subtotal</span>
            <span class="font-display text-[14px] text-white">{{ totalLabel }}</span>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="flex-1 h-[36px] rounded-[6px] bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] font-display uppercase text-[12px] text-white transition-colors border-0 cursor-pointer"
              @click="goToCheckout"
            >
              Go to checkout →
            </button>
            <button
              class="h-[36px] px-4 rounded-[6px] bg-[#272439] hover:bg-[#2f2c44] font-display uppercase text-[11px] text-[var(--color-text-dim)] hover:text-white transition-colors border-0 cursor-pointer"
              @click="cart.clear()"
            >
              Clear
            </button>
          </div>
        </footer>
      </template>
      </div>
    </div>
  </Teleport>
</template>
